/**
 * Decode a JWT payload (segment 1) and check if its `exp` claim has passed.
 *
 * Fail-closed: returns `true` (i.e. "treat as expired") for any of:
 *  - null / undefined / empty string
 *  - tokens that don't have 3 dot-separated segments
 *  - payloads that fail base64 decode or JSON parse
 *  - payloads missing the `exp` claim
 *  - payloads where `exp` is not a finite number
 *
 * Pure utility — no Angular dependencies, no localStorage, no Router.
 * AuthInterceptor is the only intended caller.
 *
 * Why no jwt-decode: the 4-line atob approach is sufficient for reading
 * `exp` and keeps the dependency surface clean.
 */
export function isTokenExpired(token: string | null | undefined): boolean {
  if (!token) {
    return true;
  }
  const segments = token.split('.');
  if (segments.length !== 3) {
    return true;
  }
  let payload: unknown;
  try {
    const base64 = segments[1].replace(/-/g, '+').replace(/_/g, '/');
    const decoded = atob(base64);
    payload = JSON.parse(decoded);
  } catch {
    return true;
  }
  const exp = (payload as { exp?: unknown })?.exp;
  if (typeof exp !== 'number' || !Number.isFinite(exp)) {
    return true;
  }
  return exp * 1000 <= Date.now();
}
